/**
 * Types for A/V streaming with audio/video calls
 */

/**
 * Star topology roles for group calls (Phase 1).
 * Hub forwards tracks between leaves. Leaf maintains 1 connection to hub.
 * Phase 2 adds 'relay' for multi-level tree.
 */
export type TreeRole = 'hub' | 'leaf';

/**
 * Tree assignment for a peer in a group call session.
 */
export interface TreeAssignment {
  role: TreeRole;
  hubAddress: string;         // Address of the hub peer
  parentAddress: string;      // Same as hubAddress in Phase 1
  childAddresses: string[];   // Only populated for hub
}

/**
 * Media stream type enumeration
 */
export enum StreamType {
  AUDIO_ONLY = 0,      // Voice call
  VIDEO_ONLY = 1,      // Screen share (future)
  AUDIO_VIDEO = 2,     // Full video call (future)
}

/**
 * Stream quality levels for adaptive streaming
 */
export enum StreamQuality {
  LOW = 0,      // 360p, 32-64kbps audio
  MEDIUM = 1,   // 720p, 64-128kbps audio
  HIGH = 2,     // 1080p, 128-256kbps audio
  AUTO = 3,     // Adaptive based on metrics
}

/**
 * Media session state lifecycle
 */
export enum MediaSessionState {
  IDLE = 'idle',
  OFFERING = 'offering',       // Sent offer, waiting for answer
  RINGING = 'ringing',         // Received offer, user deciding
  CONNECTING = 'connecting',   // Answer sent, establishing stream
  ACTIVE = 'active',           // Stream active
  ENDED = 'ended',             // Call ended
}

/**
 * Quality change history entry
 */
export interface QualityHistoryEntry {
  timestamp: number;
  quality: StreamQuality;
  reason: string; // 'bandwidth_drop', 'high_rtt', 'manual', 'initialization'
}

/**
 * Media session for tracking audio/video calls
 */
export interface MediaSession {
  sessionId: string;
  peerAddresses: string[];      // Single peer or group
  state: MediaSessionState;
  type: StreamType;
  quality: StreamQuality;
  startTime: number;
  duration: number;
  initiatedLocally: boolean;    // True if this peer initiated the call
  
  // Stream references
  localStream: MediaStream | null;
  remoteStreams: Map<string, MediaStream>; // peerAddress -> MediaStream
  
  // WebRTC media tracks
  localTransceivers: RTCRtpTransceiver[];
  remoteTransceivers: Map<string, RTCRtpTransceiver[]>; // peerAddress -> transceivers
  
  // Quality tracking
  currentStats: {
    rtt: number;
    jitter: number;
    bandwidth: number;
    packetLoss: number;
    audioLevel?: number;  // 0-1, input volume
  };
  qualityHistory: QualityHistoryEntry[];
  
  // Group call specific
  isGroupCall: boolean;
  groupAddress?: string;
  initiatorAddress?: string;  // Address of the peer who initiated the group call (moderator/organizer)
  activeParticipants: Set<string>;
  
  // Video state (for toggling video on/off after connection)
  videoEnabled: boolean;        // True if local video track is active
  remoteVideoEnabled: Map<string, boolean>; // peerAddress -> video enabled state
  
  // Screen share state
  screenShareEnabled?: boolean;           // True if local screen share is active
  screenShareTrack?: MediaStreamTrack;    // Reference to the screen capture track
  
  // Audio context for group mixing (group calls only)
  audioContext?: AudioContext;
  audioDestination?: MediaStreamAudioDestinationNode;
  participantGainNodes?: Map<string, GainNode>; // peerAddress -> GainNode
  
  // Star topology (Phase 1)
  treeRole?: TreeRole;                                        // 'hub' or 'leaf'
  hubAddress?: string;                                        // Address of the hub peer for this session
  forwardingTransceivers?: Map<string, RTCRtpTransceiver[]>;  // Hub only: childAddr -> transceivers forwarding TO that child

  // Star topology Phase 2A — origin propagation
  // Leaf-side: maps a forwarded transceiver's `mid` back to the original leaf
  // whose track it carries. Populated by GROUP_AV_TRACK_ORIGIN packets from
  // the hub. The ontrack handler consults this map to demux the forwarded
  // tracks into per-origin remote stream slots; without it every forwarded
  // track collapses into the hub's UI tile.
  trackOriginByMid?: Map<string, string>;                     // mid -> originAddress (leaf-only)

  // Buffer of remote tracks that arrived before their GROUP_AV_TRACK_ORIGIN
  // packet did. The packet handler drains this map when a matching mid
  // attribution arrives. Tracks here are NOT yet wired to remoteStreams /
  // mixer / UI — they're parked until origin is known.
  pendingAttribution?: Map<string, {                          // mid -> buffered track awaiting origin
    track: MediaStreamTrack;
    transceiver: RTCRtpTransceiver;
    streams: readonly MediaStream[];
    receivedAt: number;
  }>;

  // Hub-authored monotonic version of the membership list. Leaves drop
  // roster updates with version <= rosterVersion to ignore reordered
  // packets after a re-election.
  rosterVersion?: number;

  // Negotiated peer protocol version. Default 1 (Phase 1, legacy). 2 means
  // both ends support origin propagation; the hub will only emit
  // GROUP_AV_ROSTER / GROUP_AV_TRACK_ORIGIN to leaves whose offer/answer
  // declares >= 2.
  protocolVersion?: number;
}

/**
 * Phase 2A protocol version constant. Bump when adding new sidechannel
 * packets that older peers cannot interpret.
 */
export const AV_PROTOCOL_VERSION = 2;

/**
 * Media stream configuration for getUserMedia
 */
export interface MediaStreamConfig {
  type: StreamType;
  quality: StreamQuality;
  
  // Audio constraints
  audio: {
    echoCancellation: boolean;
    noiseSuppression: boolean;
    autoGainControl: boolean;
    sampleRate: number;  // 8000-48000 Hz
    bitrate: number;     // Adaptive: 32-256 kbps
  };
  
  // Video constraints (future)
  video?: {
    width: number;
    height: number;
    frameRate: number;  // Adaptive: 15-30 fps
    bitrate: number;    // Adaptive: 300-2500 kbps
  };
}

/**
 * A/V stream offer message payload
 */
export interface AVStreamOffer {
  sessionId: string;
  streamType: StreamType;
  proposedQuality: StreamQuality;
  hasAudio: boolean;
  hasVideo: boolean;
  groupAddress?: string; // For group calls
  hubAddress?: string;   // Star topology: address of the hub peer
  treeRole?: TreeRole;   // Star topology: role assigned to the recipient
  protocolVersion?: number; // Phase 2A: 1=legacy, 2=origin propagation supported
  timestamp: number;
}

/**
 * A/V stream answer message payload
 */
export interface AVStreamAnswer {
  sessionId: string;
  accepted: boolean;
  streamType: StreamType;
  quality: StreamQuality;
  timestamp: number;
}

/**
 * A/V stream quality update message payload
 */
export interface AVStreamUpdate {
  sessionId: string;
  newQuality: StreamQuality;
  reason: string;
  timestamp: number;
}

/**
 * A/V stream end message payload
 */
export interface AVStreamEnd {
  sessionId: string;
  reason: string; // 'hangup', 'timeout', 'error', 'network_failure'
  timestamp: number;
}

/**
 * A/V quality report message payload
 */
export interface AVQualityReport {
  sessionId: string;
  rtt: number;
  jitter: number;
  packetLoss: number;
  bandwidth: number;
  currentQuality: StreamQuality;
  timestamp: number;
}

/**
 * Hub promotion message payload (Tier 1 initiator delegates hub role)
 */
export interface AVHubPromotion {
  sessionId: string;
  groupAddress: string;
  peerAddresses: string[];   // All group members in the call
  initiatorAddress: string;  // Original initiator (for moderator tracking)
  timestamp: number;
}

/**
 * Reasons the hub broadcasts a GROUP_AV_ROSTER packet. Leaves use this to
 * decide whether the update represents a peer joining (refresh UI), a peer
 * leaving (drop their UI tile but keep P2P data channels alive), or a
 * full session end (tear down everything).
 */
export type AVGroupRosterReason =
  | 'session-start'
  | 'leaf-join'
  | 'leaf-leave'
  | 'hub-promotion'
  | 'group-end';

/**
 * Phase 2A: hub-authoritative roster broadcast.
 * Sent on session start, leaf join, leaf leave, hub re-election, and group end.
 */
export interface AVGroupRoster {
  sessionId: string;
  groupAddress: string;
  hubAddress: string;
  members: string[];                // All session members including hub
  rosterVersion: number;            // Monotonic per session
  reason: AVGroupRosterReason;
  timestamp: number;
}

/**
 * Phase 2A: hub→leaf mapping of a forwarded transceiver to its origin leaf.
 * Sent immediately after the hub adds a forwarding transceiver to a leaf's
 * connection. The leaf consults its `trackOriginByMid` map at ontrack time
 * to attribute the forwarded track to the correct UI tile.
 *
 * relayPath is forward-compatible with multi-hop tree topology (Phase 3A):
 * a branch peer extends the path before forwarding to its leaves.
 */
export interface AVTrackOrigin {
  sessionId: string;
  mid: string;                      // RTCRtpTransceiver.mid
  originAddress: string;
  trackKind: 0 | 1 | 2;             // 0=audio, 1=video, 2=data (future)
  relayPath: string[];              // [hubAddress] in Phase 2A
  timestamp: number;
}

/**
 * Bandwidth allocation result
 */
export interface BandwidthAllocation {
  avBandwidth: number;      // Bytes/sec allocated to A/V
  fileBandwidth: number;    // Bytes/sec allocated to file transfers
  totalBandwidth: number;   // Total available
}
